import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Button,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Paper,
  ClickAwayListener,
  CircularProgress,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  VolumeUp,
  VolumeOff,
  Search as SearchIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  FileUploadOutlined as FileUploadOutlinedIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import Logo from './Logo';
import { fetchCompanyAutocomplete, setSelectedCompanyDomain } from '../utils/api';
import { COLORS, COMPONENT_COLORS } from '../config/colors';

// Company Dropdown Component
const CompanyDropdown = ({ onCompanySelect, defaultCompany }) => {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState(defaultCompany || '')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  // Initialize selectedCompany as an object if defaultCompany is a string
  const [selectedCompany, setSelectedCompany] = useState(
    typeof defaultCompany === 'string' 
      ? { name: defaultCompany } 
      : defaultCompany || null
  )

  // Set the default company when it changes
  useEffect(() => {
    if (defaultCompany && !selectedCompany) {
      // Convert string to object if needed
      const companyObj = typeof defaultCompany === 'string' 
        ? { name: defaultCompany } 
        : defaultCompany;
      
      setSelectedCompany(companyObj)
      setInputValue(typeof companyObj === 'object' ? companyObj.name : companyObj)
    }
  }, [defaultCompany, selectedCompany])
  const searchTimeout = useRef(null)

  // Fetch company suggestions when input value changes
  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([])
      return
    }

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    // Set a timeout to avoid making too many API calls
    searchTimeout.current = setTimeout(async () => {
      setLoading(true)
      try {
        const results = await fetchCompanyAutocomplete(inputValue)
        /*
        each result item will look like below - domain, name etc. 
        {
    "_id": "63efa300df8d8b6507146358",
    "score": 1.9992107711096065,
    "ref": "5b0c4a8f3200621bdb2ef877",
    "sname": "paypal",
    "name": "PayPal",
    "domain": "paypal.com"
    }*/
        // Keep the full company objects to access domain later
        setOptions(Array.isArray(results) ? results : [])
      } catch (error) {
        console.error('Error fetching company suggestions:', error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
    }
  }, [inputValue])

  // Handle company selection
  const handleCompanySelect = (event, company) => {
    if (!company) {
      // Handle case when selection is cleared
      setSelectedCompany(null)
      setOpen(false)
      if (onCompanySelect) {
        onCompanySelect(null)
      }
      return;
    }
    
    // Convert to object if it's a string
    let companyObj = company;
    if (typeof company === 'string') {
      // Try to find matching company in options
      const matchingOption = options.find(opt => opt.name === company);
      companyObj = matchingOption || { name: company };
    }
    
    // Update input value to show the company name
    setInputValue(companyObj.name)
    
    // Store the company object
    setSelectedCompany(companyObj)
    setOpen(false)
    
    if (onCompanySelect) {
      onCompanySelect(companyObj)
    }
  }

  return (
    <Box sx={{ position: 'relative', width: 250 }}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false)
          setIsTyping(false)
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          setInputValue(newInputValue)
          // Set typing state based on the reason for input change
          setIsTyping(reason === 'input')
        }}
        onChange={handleCompanySelect}
        value={selectedCompany}
        options={options}
        loading={loading}
        // Allow free text input without validation errors
        freeSolo
        // Don't select on focus to prevent unwanted selections
        selectOnFocus={false}
        // Don't auto-select the first option
        autoSelect={false}
        // Don't clear on blur
        clearOnBlur={false}
        getOptionLabel={option => (typeof option === 'object' ? option.name : option) || ''}
        // Add this prop to customize equality check
        isOptionEqualToValue={(option, value) => {
          // If user is typing, don't validate to prevent console errors
          if (isTyping) {
            return true;
          }
          
          // If either is null, they're equal only if both are null
          if (option === null || value === null) {
            return option === value;
          }
          
          // If value is a string, compare with option.name
          if (typeof value === 'string') {
            return option.name === value;
          }
          
          // If both are objects with _id, compare by _id (most reliable)
          if (typeof option === 'object' && typeof value === 'object') {
            // If both have _id, compare by _id
            if (option._id && value._id) {
              return option._id === value._id;
            }
            
            // If both have name, compare by name
            if (option.name && value.name) {
              return option.name === value.name;
            }
            
            // If one has name and the other is a string, compare those
            if (option.name && typeof value === 'string') {
              return option.name === value;
            }
            
            if (value.name && typeof option === 'string') {
              return value.name === option;
            }
          }
          
          // Default comparison
          return option === value;
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder='Search company...'
            variant='outlined'
            size='small'
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <BusinessIcon
                    sx={{ color: '#9CA3AF', fontSize: 20 }} // Updated icon color to match Figma design
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#1F2937', // Updated background color to match Figma design
                color: '#E5E7EB', // Updated text color to match Figma design
                '&:hover': {
                  backgroundColor: '#374151' // Updated hover color to match Figma design
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#374151' // Updated border color to match Figma design
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4B5563' // Updated hover border color to match Figma design
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3B82F6', // Updated focus border color to match Figma design
                }
              },
              '& .MuiInputLabel-root': {
                color: '#9CA3AF' // Updated label color to match Figma design
              }
            }}
          />
        )}
        renderOption={(props, option, index) => (
          <li {...props} key={`company-option-${typeof option === 'object' && option._id ? option._id : index}`}>
            <Typography variant='body2' sx={{ color: '#E5E7EB' }}> {/* Updated text color to match Figma design */}
              {typeof option === 'object' ? option.name : option}
            </Typography>
          </li>
        )}
        PaperComponent={props => (
          <Paper
            elevation={3}
            {...props}
            sx={{
              borderRadius: 2,
              mt: 1,
              maxHeight: 300,
              overflow: 'auto',
              backgroundColor: '#1F2937', // Updated background color to match Figma design
              border: '1px solid #374151' // Added border to match Figma design
            }}
          />
        )}
        noOptionsText='No companies found'
        sx={{ width: '100%' }}
      />
    </Box>
  )
}

const Header = ({
  isAudioReady,
  setIsAudioReady,
  setSessionName,
  sessionName,
  onSaveChat,
  userInfo,
  rightSidebarOpen,
  rightSidebarWidth = 456,
  leftSidebarOpen,
  drawerWidth = 250,
  collapsedWidth = 50,
  currentDiscoveryState,
  convoStarted = false,
  onNavigateToDefineRole,
  onNavigateToReviewMatches,
  onNavigateToSendOutreach,
  matchCount = null,
  matchResults = null
}) => {
  const [selectedCompany, setSelectedCompany] = useState(
    typeof userInfo?.company === 'string' 
      ? { name: userInfo?.company } 
      : userInfo?.company || null
  )
  
  // Handle company selection in the Header component
  const handleCompanySelect = (company) => {
    // Always store the full company object
    setSelectedCompany(company);
    
    // Set the selected company domain in the global store
    if (company && company.domain) {
      setSelectedCompanyDomain(company.domain);
      console.log('Selected company domain set to:', company.domain);
    }
  }
  const [open, setOpen] = useState(false) // Dialog state
  const [editTitle, setEditTitle] = useState(sessionName) // Temporary state for editing session name
  const [description, setDescription] = useState('') // Temporary state for description

  // Open dialog and set the current session name in edit state
  const handleEditClick = () => {
    setEditTitle(sessionName)
    setOpen(true)
  }

  // Close dialog without saving changes
  const handleClose = () => {
    setOpen(false)
    setEditTitle(sessionName) // Reset to original name
    setDescription('') // Clear description
  }

  // Save changes to session name and description
  const handleSave = () => {
    // Only call onSaveChat, which will handle both setting the name and saving
    onSaveChat(editTitle) // Save the chat with the new name
    setOpen(false) // Close dialog
  }

  // Handle changes to session name input
  const handleTitleChange = event => {
    setEditTitle(event.target.value)
  }

  // Handle changes to description input
  const handleDescriptionChange = event => {
    setDescription(event.target.value)
  }

  // Toggle audio ready state (speaker enabled/disabled)
  const handleAudioToggle = () => {
    setIsAudioReady(prev => !prev)
  }

  // Determine the current step and completion status based on discovery state
  const getStepStatus = () => {
    // If conversation hasn't started, show initial state
    if (!convoStarted) {
      return {
        defineRoleCompleted: false,
        defineRoleActive: true,
        reviewMatchesActive: false,
        sendOutreachActive: false,
        reviewMatchesEnabled: false,
        sendOutreachEnabled: false
      }
    }

    // Check if search has been created (same logic as IntakeSummary)
    const hasSearchResults = matchCount && matchCount.base_filter_count > 0;

    // Determine current view based on discovery state
    const currentTopic = currentDiscoveryState?.currentDiscoTopic;
    
    // Check if we have completed the role definition phase
    const hasRoleDefinition = currentTopic && 
      (currentTopic === 'candidate_preview' || currentTopic === 'messaging');
    
    // Determine which view is currently active
    const defineRoleActive = !currentTopic || (currentTopic !== 'candidate_preview' && currentTopic !== 'messaging');
    const reviewMatchesActive = currentTopic === 'candidate_preview';
    const sendOutreachActive = currentTopic === 'messaging';
    
    // Role definition is completed if we've moved beyond it
    const defineRoleCompleted = hasRoleDefinition;

    // Review Matches and Send Outreach are only enabled if search results exist
    const reviewMatchesEnabled = hasSearchResults;
    const sendOutreachEnabled = hasSearchResults;

    return {
      defineRoleCompleted,
      defineRoleActive,
      reviewMatchesActive,
      sendOutreachActive,
      reviewMatchesEnabled,
      sendOutreachEnabled
    }
  }

  const stepStatus = getStepStatus();

  return (
    <AppBar
      position='fixed'
      sx={{
        borderBottom: '1px solid #DCDFEA', // Layout V2 Default Border color
        boxShadow: 'none',
        background: 'white', // Simple white background
        zIndex: 1200,
        marginLeft:0.5,
        width: `calc(100% - ${leftSidebarOpen ? drawerWidth : collapsedWidth}px)`,
        maxWidth: '100%', // Ensure responsiveness
        transition: 'width 0.3s ease-in-out, left 0.3s ease-in-out',
        top: 0,
        left: leftSidebarOpen ? `${drawerWidth}px` : `${collapsedWidth}px`,
        height: '80px', // Fixed height to match Figma design
        color:'black'
      }}
    >
      <Toolbar 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'transparent', // Transparent background to let the gradient show through
          padding: '0 40px', // Match Figma padding
          gap: '12px', // Set gap between sections
          width: '98%', // Use full width of the AppBar
          maxWidth: '100%', // Ensure responsiveness
          minHeight: '72px', // Match Figma height
          height: '72px', // Fixed height
        }}
      >
        {/* Title Section */}
        <Box sx={{ display: 'flex', alignItems: 'center'}}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #4A2EDF 25%, #1E49BB 50%, #D4C4FC 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: '1.171875em',
                }}
              >
                Hiring Agent
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Navigation Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Chip
            // icon={stepStatus.defineRoleCompleted ? <CheckCircleIcon sx={{ fontSize: '18px !important' }} /> : undefined}
            label="Define Role"
            onClick={onNavigateToDefineRole}
            clickable
            sx={{
              backgroundColor: stepStatus.defineRoleActive ? '#E1EFFF' : '#F9FAFB',
              color: stepStatus.defineRoleActive ? '#0F42BC' : '#969DAD',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '1.4285714285714286em',
              borderRadius: '8px',
              padding: '8px 14px',
              height: 'auto',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: stepStatus.defineRoleActive ? '#D1E7FF' : '#F3F4F6',
              },
              '& .MuiChip-icon': {
                color: '#00B093',
                marginLeft: '4px',
                marginRight: '4px',
              },
              '& .MuiChip-label': {
                paddingLeft: stepStatus.defineRoleCompleted ? '4px' : '14px',
                paddingRight: '14px',
              },
            }}
          />
          <Chip
            label="Review Matches"
            onClick={stepStatus.reviewMatchesEnabled ? onNavigateToReviewMatches : undefined}
            clickable={stepStatus.reviewMatchesEnabled}
            sx={{
              backgroundColor: stepStatus.reviewMatchesActive ? '#E1EFFF' : '#F9FAFB',
              color: stepStatus.reviewMatchesEnabled 
                ? (stepStatus.reviewMatchesActive ? '#0F42BC' : '#969DAD')
                : '#C4C4C4',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '1.4285714285714286em',
              borderRadius: '8px',
              padding: '8px 14px',
              height: 'auto',
              cursor: stepStatus.reviewMatchesEnabled ? 'pointer' : 'not-allowed',
              opacity: stepStatus.reviewMatchesEnabled ? 1 : 0.5,
              '&:hover': stepStatus.reviewMatchesEnabled ? {
                backgroundColor: stepStatus.reviewMatchesActive ? '#D1E7FF' : '#F3F4F6',
              } : {},
              '& .MuiChip-label': {
                paddingLeft: '14px',
                paddingRight: '14px',
              },
            }}
          />
          <Chip
            label="Send Outreach"
            onClick={stepStatus.sendOutreachEnabled ? onNavigateToSendOutreach : undefined}
            clickable={stepStatus.sendOutreachEnabled}
            sx={{
              backgroundColor: stepStatus.sendOutreachActive ? '#E1EFFF' : '#F9FAFB',
              color: stepStatus.sendOutreachEnabled 
                ? (stepStatus.sendOutreachActive ? '#0F42BC' : '#969DAD')
                : '#C4C4C4',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '1.4285714285714286em',
              borderRadius: '8px',
              padding: '8px 14px',
              height: 'auto',
              cursor: stepStatus.sendOutreachEnabled ? 'pointer' : 'not-allowed',
              opacity: stepStatus.sendOutreachEnabled ? 1 : 0.5,
              '&:hover': stepStatus.sendOutreachEnabled ? {
                backgroundColor: stepStatus.sendOutreachActive ? '#D1E7FF' : '#F3F4F6',
              } : {},
              '& .MuiChip-label': {
                paddingLeft: '14px',
                paddingRight: '14px',
              },
            }}
          />
        </Box>

        {/* Control Buttons and Company Dropdown */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}> {/* Increased gap */}
          {/* Section Heading Actions */}
          {/* Company Dropdown */}
          <CompanyDropdown
            onCompanySelect={handleCompanySelect}
            defaultCompany={userInfo?.company}
          />

          {/* Speaker Toggle Button (hidden) */}
          <Box sx={{ display: 'none' }}>
            <Button
              onClick={handleAudioToggle}
              variant='contained'
              startIcon={isAudioReady ? <VolumeUp /> : <VolumeOff />}
              sx={{
                backgroundColor: isAudioReady ? '#10B981' : '#6B7280', // Updated button colors to match Figma design
                '&:hover': {
                  backgroundColor: isAudioReady ? '#059669' : '#4B5563' // Updated hover colors to match Figma design
                },
                color: 'white',
                textTransform: 'none'
              }}
            >
              {isAudioReady ? 'Speaker Enabled' : 'Enable Speaker'}
            </Button>
          </Box>
          
        </Box>
      </Toolbar>

      {/* Dialog for Editing Title */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1F2937', // Updated background color to match Figma design
            color: '#E5E7EB', // Updated text color to match Figma design
            borderRadius: 2 // Added border radius to match Figma design
          }
        }}
      >
        <DialogTitle sx={{ color: '#E5E7EB' }}>Edit Session Details</DialogTitle> {/* Updated text color */}
        <DialogContent>
          <TextField
            label='Session Name'
            variant='outlined'
            fullWidth
            margin='normal'
            value={editTitle}
            onChange={handleTitleChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#E5E7EB', // Updated text color to match Figma design
                borderColor: '#374151', // Updated border color to match Figma design
                '& fieldset': {
                  borderColor: '#374151', // Updated border color to match Figma design
                },
                '&:hover fieldset': {
                  borderColor: '#4B5563', // Updated hover border color to match Figma design
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3B82F6', // Updated focus border color to match Figma design
                }
              },
              '& .MuiInputLabel-root': {
                color: '#9CA3AF' // Updated label color to match Figma design
              }
            }}
          />
          <TextField
            label='Description'
            variant='outlined'
            fullWidth
            margin='normal'
            multiline
            rows={3}
            value={description}
            onChange={handleDescriptionChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#E5E7EB', // Updated text color to match Figma design
                borderColor: '#374151', // Updated border color to match Figma design
                '& fieldset': {
                  borderColor: '#374151', // Updated border color to match Figma design
                },
                '&:hover fieldset': {
                  borderColor: '#4B5563', // Updated hover border color to match Figma design
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3B82F6', // Updated focus border color to match Figma design
                }
              },
              '& .MuiInputLabel-root': {
                color: '#9CA3AF' // Updated label color to match Figma design
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose} 
            sx={{ 
              color: '#E5E7EB', // Updated text color to match Figma design
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)' // Added hover background color
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant='contained'
            sx={{ 
              backgroundColor: '#3B82F6', // Updated button color to match Figma design
              '&:hover': {
                backgroundColor: '#2563EB' // Updated hover color to match Figma design
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  )
}

export default Header
